import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useEntity } from "@/lib/entityStore";
import { useAuth } from "@/lib/auth";
import { Camera, Mail } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section } from "@/components/ds/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Settings() {
    const { entities, refreshEntities } = useEntity();
    const { user } = useAuth();
    const [drafts, setDrafts] = useState({});
    const [briefingSettings, setBriefingSettings] = useState(null);
    const [bsForm, setBsForm] = useState({ recipients: "", from_email: "" });
    const [snapBusy, setSnapBusy] = useState(false);
    const [bsBusy, setBsBusy] = useState(false);

    useEffect(() => {
        const d = {};
        for (const e of entities) {
            d[e.id] = {
                display_name: e.display_name,
                legal_name: e.legal_name,
                notes: e.notes || "",
            };
        }
        setDrafts(d);
    }, [entities]);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/settings/briefings");
            setBriefingSettings(data);
            setBsForm({
                recipients: (data.recipients || []).join(", "),
                from_email: data.from_email || "",
            });
        })();
    }, []);

    const save = async (e) => {
        try {
            await api.patch(`/entities/${e.id}`, drafts[e.id]);
            toast.success(`${drafts[e.id].display_name} updated`);
            await refreshEntities();
        } catch {
            toast.error("Could not save entity");
        }
    };

    const captureSnapshot = async (kind) => {
        setSnapBusy(true);
        try {
            const { data } = await api.post("/snapshots/capture", { kind });
            toast.success(`Captured ${kind} snapshots — ${data.entity_snapshot_count} entities + 1 group`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Snapshot failed");
        } finally {
            setSnapBusy(false);
        }
    };

    const saveBriefings = async () => {
        setBsBusy(true);
        try {
            const recipients = bsForm.recipients.split(",").map((r) => r.trim()).filter(Boolean);
            await api.patch("/settings/briefings", {
                recipients,
                from_email: bsForm.from_email || null,
            });
            const { data } = await api.get("/settings/briefings");
            setBriefingSettings(data);
            toast.success("Briefing settings saved");
        } catch {
            toast.error("Could not save briefing settings");
        } finally {
            setBsBusy(false);
        }
    };

    return (
        <PageShell width="default" testId="settings-page">
            <PageHeader
                eyebrow="Configuration"
                title="Settings"
                subtitle="Entity naming, briefing recipients, snapshot control. Role management and write-back to MYOB are deferred."
            />

            <Section title="Account">
                <Card variant="default" padding="lg" testId="settings-user">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <div className="label-soft text-stone-500 mb-1">Email</div>
                            <div className="font-mono text-[13.5px] text-stone-800">{user?.email}</div>
                        </div>
                        <div>
                            <div className="label-soft text-stone-500 mb-1">Role</div>
                            <div className="text-[14px] text-stone-800 capitalize">{user?.role?.replace("_", " ")}</div>
                        </div>
                        <div>
                            <div className="label-soft text-stone-500 mb-1">Organisation</div>
                            <div className="text-[14px] text-stone-800">All Crane Sales</div>
                        </div>
                    </div>
                </Card>
            </Section>

            <Section
                title="Snapshot engine"
                subtitle="Daily auto snapshots are captured on backend startup. Manual snapshots are useful for marking a moment before a board meeting or material decision."
            >
                <Card variant="default" padding="lg" testId="settings-snapshots">
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            variant="outline"
                            className="rounded-md h-9"
                            onClick={() => captureSnapshot("auto")}
                            disabled={snapBusy}
                            data-testid="snap-auto"
                        >
                            <Camera size={13} className="mr-2" strokeWidth={2} />
                            Refresh today's auto snapshot
                        </Button>
                        <Button
                            className="rounded-md h-9"
                            style={{ background: "var(--brand)", color: "white" }}
                            onClick={() => captureSnapshot("manual")}
                            disabled={snapBusy}
                            data-testid="snap-manual"
                        >
                            <Camera size={13} className="mr-2" strokeWidth={2} />
                            Capture manual snapshot
                        </Button>
                    </div>
                </Card>
            </Section>

            <Section
                title="Briefing email"
                subtitle="Email is optional. Briefings are always generated and archived in-app. Configure Resend to enable delivery."
                action={
                    <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border rounded-full ${
                            briefingSettings?.configured
                                ? "bg-emerald-50 text-emerald-800 border-emerald-100"
                                : "bg-amber-50 text-amber-800 border-amber-100"
                        }`}
                        data-testid="resend-status"
                    >
                        <Mail size={11} strokeWidth={2} />
                        {briefingSettings?.configured ? "Configured" : "Not configured"}
                    </span>
                }
            >
                <Card variant="default" padding="lg" testId="settings-briefings">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Recipients (comma-separated)</Label>
                            <Input
                                className="rounded-md h-9 mt-1 font-mono text-[13px]"
                                value={bsForm.recipients}
                                onChange={(e) => setBsForm((s) => ({ ...s, recipients: e.target.value }))}
                                placeholder="owner@allcrane.com, accountant@allcrane.com"
                                data-testid="brief-recipients"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">From email</Label>
                            <Input
                                className="rounded-md h-9 mt-1 font-mono text-[13px]"
                                value={bsForm.from_email}
                                onChange={(e) => setBsForm((s) => ({ ...s, from_email: e.target.value }))}
                                placeholder="briefings@allcrane.com"
                                data-testid="brief-from"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <Button
                            className="rounded-md h-9 text-[13px]"
                            style={{ background: "var(--brand)", color: "white" }}
                            onClick={saveBriefings}
                            disabled={bsBusy}
                            data-testid="brief-save"
                        >
                            Save
                        </Button>
                    </div>
                </Card>
            </Section>

            <Section
                title="Entity naming"
                subtitle="Display name, legal name and a freeform context note per entity."
            >
                <div className="space-y-3">
                    {entities.map((e) => (
                        <Card
                            key={e.id}
                            variant="default"
                            padding="lg"
                            testId={`entity-settings-${e.entity_number}`}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Display name</Label>
                                    <Input
                                        className="rounded-md h-9 mt-1"
                                        value={drafts[e.id]?.display_name || ""}
                                        onChange={(ev) =>
                                            setDrafts((d) => ({
                                                ...d,
                                                [e.id]: { ...d[e.id], display_name: ev.target.value },
                                            }))
                                        }
                                        data-testid={`input-display-${e.entity_number}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Legal name</Label>
                                    <Input
                                        className="rounded-md h-9 mt-1"
                                        value={drafts[e.id]?.legal_name || ""}
                                        onChange={(ev) =>
                                            setDrafts((d) => ({
                                                ...d,
                                                [e.id]: { ...d[e.id], legal_name: ev.target.value },
                                            }))
                                        }
                                    />
                                </div>
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Management context</Label>
                                    <Textarea
                                        className="rounded-md mt-1 resize-none"
                                        rows={2}
                                        value={drafts[e.id]?.notes || ""}
                                        onChange={(ev) =>
                                            setDrafts((d) => ({
                                                ...d,
                                                [e.id]: { ...d[e.id], notes: ev.target.value },
                                            }))
                                        }
                                    />
                                </div>
                            </div>
                            <div className="mt-4 flex justify-end">
                                <Button
                                    onClick={() => save(e)}
                                    className="rounded-md h-9 text-[13px]"
                                    style={{ background: "var(--brand)", color: "white" }}
                                    data-testid={`save-${e.entity_number}`}
                                >
                                    Save
                                </Button>
                            </div>
                        </Card>
                    ))}
                </div>
            </Section>
        </PageShell>
    );
}
